// Modal "Gerenciar acessos" — CRUD de membros + geração de convite.
//
// Abertura: botão na sidebar do detail (ver index.html) chama
// window.openMembersModal().
//
// Features:
//   - Lista members do trip atual com role e botão remover (só admin)
//   - Gera invite doc em `invites/{token}` e mostra URL + botão WhatsApp
//   - Lista invites ativos com contador de uso + botão revogar
//
// Rules autorizam: create/update/delete de invites só pelo admin; update de
// memberIds/members só pelo admin. Operações são feitas direto via Firestore
// SDK — sem backend.

import { auth } from '../../core/firebase.js';
import { db } from '../../core/firebase.js';
import {
  collection, doc, getDoc, setDoc, deleteDoc, updateDoc,
  query, where, getDocs, serverTimestamp, arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { createUserInviteAutocomplete } from '../../shared/components/userInviteAutocomplete.js';
import { inviteUrl } from '../../core/site.js';

const _tripId = () => new URLSearchParams(location.search).get('trip');

function _randomToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

function _buildInviteUrl(token) {
  // Base pública do site (não location.origin — no app é localhost).
  return inviteUrl(token);
}

function _whatsappUrl(inviteUrl, tripName, tripEmoji) {
  const msg = `Oi! Te convidei pra nossa viagem no Mapiare — ${tripEmoji || '🌍'} ${tripName}. Aceita aqui: ${inviteUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
}

async function _loadTrip(tripId) {
  if (!tripId) throw new Error('tripId obrigatório');
  const snap = await getDoc(doc(db, 'trips', tripId));
  if (!snap.exists()) throw new Error('trip não encontrado');
  return { id: snap.id, ...snap.data() };
}

async function _loadActiveInvites(tripId) {
  const q = query(collection(db, 'invites'), where('tripId', '==', tripId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// tripId em módulo scope — preenchido na abertura do modal. Usado pelas
// actions (generateInvite, removeMember, revokeInvite) que são chamadas
// via onclick inline.
let _activeTripId = null;

// Instância atual do autocomplete de convite (componente compartilhado).
// Precisa de cleanup (destroy) — registra um listener global de pointerdown.
let _inviteAutocomplete = null;

/**
 * Abre o modal. tripId opcional — se não passado, usa da URL (contexto
 * do trip detail). Na home, passa o tripId explicitamente.
 */
export async function openMembersModal(tripId) {
  _activeTripId = tripId || _tripId();
  if (!_activeTripId) { alert('Nenhuma viagem selecionada.'); return; }
  // Lazy render
  let overlay = document.getElementById('membersModal');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'membersModal';
    overlay.className = 'members-overlay';
    overlay.innerHTML = `
      <div class="members-card">
        <div class="members-head">
          <h3>🤝 Gerenciar acessos</h3>
          <button class="members-close" onclick="closeMembersModal()">✕</button>
        </div>
        <div class="members-body" id="membersBody">
          <p style="color:var(--text-secondary);font-size:13px">Carregando...</p>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay) closeMembersModal(); });
  }
  overlay.classList.add('show');

  try {
    await _renderMembersBody();
  } catch (e) {
    console.error('openMembersModal:', e);
    document.getElementById('membersBody').innerHTML = `<p style="color:var(--danger)">Erro: ${e.message}</p>`;
  }
}

export function closeMembersModal() {
  document.getElementById('membersModal')?.classList.remove('show');
  // Limpa o listener global registrado pelo autocomplete de convite.
  if (_inviteAutocomplete) {
    try { _inviteAutocomplete.destroy(); } catch {}
    _inviteAutocomplete = null;
  }
}

async function _renderMembersBody() {
  const trip = await _loadTrip(_activeTripId);
  const uid = auth.currentUser?.uid;
  const isAdmin = trip.userId === uid;
  const members = trip.members || {};
  const memberIds = trip.memberIds || [];
  const invites = await _loadActiveInvites(trip.id);
  const body = document.getElementById('membersBody');

  // ── Lista de members ─────────────────────────────────────────────
  const membersHtml = memberIds.map(mId => {
    const m = members[mId] || { email: '(sem email)', role: 'member' };
    const isSelf = mId === uid;
    const isAdminRole = m.role === 'admin';
    const removeBtn = isAdmin && !isSelf && !isAdminRole
      ? `<button class="members-remove-btn" onclick="removeMember('${mId}')">Remover</button>`
      : '';
    return `
      <div class="members-row">
        <div class="members-info">
          <div class="members-email">${_esc(m.email || '(sem email)')}${isSelf ? ' <span style="color:var(--text-tertiary);font-weight:400">(você)</span>' : ''}</div>
          <div class="members-role">${isAdminRole ? '👑 Admin' : '👥 Membro'}</div>
        </div>
        ${removeBtn}
      </div>
    `;
  }).join('');

  const atLimit = memberIds.length >= 10;

  // ── Invites ativos ───────────────────────────────────────────────
  const invitesList = invites
    .filter(i => !i.revoked)
    .map(i => `
      <div class="invites-row">
        <div class="invites-info">
          <div class="invites-date">${i.email
            ? `Convite para ${_esc(i.email)}`
            : `Link criado em ${_fmtDate(i.createdAt)}`}</div>
          <div class="invites-url">${_buildInviteUrl(i.id)}</div>
        </div>
        ${isAdmin ? `<button class="members-remove-btn" onclick="revokeInvite('${i.id}')">Revogar</button>` : ''}
      </div>
    `).join('');

  body.innerHTML = `
    <div class="members-section">
      <div class="members-section-title">Membros (${memberIds.length}/10)</div>
      <div class="members-list">${membersHtml}</div>
    </div>

    ${isAdmin ? `
      <div class="members-section">
        <div class="members-section-title">Convidar nova pessoa</div>
        ${atLimit
          ? '<p style="font-size:13px;color:var(--text-secondary)">Limite de 10 membros atingido. Remova alguém pra liberar vaga.</p>'
          : `
            <div id="membersInviteMount"></div>
            <p style="font-size:12px;color:var(--text-tertiary);margin-top:8px">
              Busque alguém pelo nome ou email — ou digite um email e aperte Enter.
              Gera um link único pra compartilhar por WhatsApp ou copiar.
            </p>`
        }

        ${invitesList ? `
          <div style="margin-top:20px">
            <div class="members-section-title" style="font-size:13px">Links ativos</div>
            <div class="invites-list">${invitesList}</div>
          </div>
        ` : ''}
      </div>
    ` : ''}
  `;

  // Monta o autocomplete de convite (componente compartilhado) no mount point.
  if (isAdmin && !atLimit) {
    _mountInviteAutocomplete(trip, members);
  }
}

// Destrói o autocomplete anterior (se houver) e monta um novo. Chamado a cada
// re-render do modal — o componente registra listener global, então precisa
// de cleanup pra não vazar.
function _mountInviteAutocomplete(trip, members) {
  const mount = document.getElementById('membersInviteMount');
  if (!mount) return;

  if (_inviteAutocomplete) {
    try { _inviteAutocomplete.destroy(); } catch {}
    _inviteAutocomplete = null;
  }

  // Emails que já são membros — não sugerir / não reconvidar.
  const memberEmails = Object.values(members || {})
    .map((m) => m.email)
    .filter(Boolean);

  _inviteAutocomplete = createUserInviteAutocomplete({
    placeholder: 'Nome ou email da pessoa',
    excludeEmails: memberEmails,
    onInvite: async ({ email }) => {
      // Toda a lógica de domínio (gerar token, criar invites/{token} amarrado
      // ao email) fica AQUI, no caller — o componente é genérico.
      const url = await _createEmailInvite(trip.id, email);
      _showInviteGenerated(url, trip.name, trip.coverEmoji, email);
      await _renderMembersBody();
    },
  });
  mount.appendChild(_inviteAutocomplete.element);
}

function _esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function _fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR');
}

// ─── Actions expostas no window (onclick) ─────────────────────────────

/**
 * Cria um doc `invites/{token}` AMARRADO a um email. Reutilizado pelo
 * autocomplete de convite. O campo `email` liga o convite ao endereço:
 * quem entrar/cadastrar com esse email cai na viagem. O link continua
 * sendo gerado normalmente (token-based) — nenhum email é enviado.
 *
 * @param {string} tripId
 * @param {string} email  email alvo (existente ou não no Mapiare)
 * @returns {Promise<string>} URL do convite
 */
async function _createEmailInvite(tripId, email) {
  const trip = await _loadTrip(tripId);
  const token = _randomToken();
  const adminEmail = auth.currentUser?.email || null;
  // Snapshot do trip no invite: o convidado (ainda sem acesso ao trip doc)
  // precisa ver nome/emoji/datas pra saber o que tá aceitando.
  const tripSummary = {
    name: trip.name || 'Viagem',
    coverEmoji: trip.coverEmoji || '🌍',
    startDate: trip.startDate || null,
    endDate: trip.endDate || null,
  };
  await setDoc(doc(db, 'invites', token), {
    tripId: trip.id,
    createdBy: auth.currentUser.uid,
    createdByEmail: adminEmail,
    email: email || null,        // convite amarrado a este endereço
    tripSummary,
    createdAt: serverTimestamp(),
    expiresAt: null,
    revoked: false,
  });
  return _buildInviteUrl(token);
}

export async function generateInvite() {
  try {
    const trip = await _loadTrip(_activeTripId);
    // Convite sem email alvo (link aberto) — mantém compat com o botão antigo.
    const url = await _createEmailInvite(trip.id, null);
    _showInviteGenerated(url, trip.name, trip.coverEmoji);
    // Re-render modal pra mostrar o novo link na lista
    await _renderMembersBody();
  } catch (e) {
    console.error('generateInvite:', e);
    alert('Erro ao gerar convite: ' + e.message);
  }
}

function _showInviteGenerated(url, tripName, tripEmoji, targetEmail) {
  const wa = _whatsappUrl(url, tripName, tripEmoji);
  const overlay = document.createElement('div');
  overlay.className = 'invite-popup-overlay';
  const forWhom = targetEmail
    ? `Convite para <strong>${_esc(targetEmail)}</strong>. Compartilhe o link abaixo:`
    : 'Compartilhe com quem você quer convidar:';
  overlay.innerHTML = `
    <div class="invite-popup">
      <h3>🔗 Link gerado</h3>
      <p style="font-size:13px;color:var(--text-secondary);margin-bottom:12px">${forWhom}</p>
      <input type="text" readonly value="${_esc(url)}" id="inviteUrlInput" style="width:100%;padding:10px;font-family:monospace;font-size:12px;background:var(--input-bg);border:1px solid var(--border);border-radius:var(--r-md);color:var(--text-primary);margin-bottom:12px"/>
      <div style="display:flex;gap:8px">
        <button class="members-primary-btn" onclick="copyInviteUrl()" style="flex:1">📋 Copiar</button>
        <a href="${_esc(wa)}" target="_blank" class="members-primary-btn" style="flex:1;text-align:center;text-decoration:none;display:inline-block">💬 WhatsApp</a>
      </div>
      <button class="members-close-btn" onclick="this.closest('.invite-popup-overlay').remove()" style="margin-top:12px">Fechar</button>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

export function copyInviteUrl() {
  const input = document.getElementById('inviteUrlInput');
  if (!input) return;
  input.select();
  navigator.clipboard.writeText(input.value).then(() => {
    const btns = document.querySelectorAll('.invite-popup button');
    btns.forEach(b => { if (b.textContent.includes('Copiar')) b.textContent = '✅ Copiado!'; });
  });
}

export async function removeMember(memberUid) {
  if (!confirm('Remover este membro da viagem?')) return;
  try {
    const trip = await _loadTrip(_activeTripId);
    await updateDoc(doc(db, 'trips', trip.id), {
      memberIds: arrayRemove(memberUid),
      [`members.${memberUid}`]: deleteField(),
    });
    await _renderMembersBody();
  } catch (e) {
    console.error('removeMember:', e);
    alert('Erro ao remover: ' + e.message);
  }
}

export async function revokeInvite(token) {
  if (!confirm('Revogar este link? Quem ainda não aceitou não consegue mais.')) return;
  try {
    await updateDoc(doc(db, 'invites', token), { revoked: true });
    await _renderMembersBody();
  } catch (e) {
    console.error('revokeInvite:', e);
    alert('Erro ao revogar: ' + e.message);
  }
}

// Expõe no window pros onclick inline
globalThis.openMembersModal = openMembersModal;
globalThis.closeMembersModal = closeMembersModal;
globalThis.generateInvite = generateInvite;
globalThis.copyInviteUrl = copyInviteUrl;
globalThis.removeMember = removeMember;
globalThis.revokeInvite = revokeInvite;
